import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Swords, ShieldCheck, MailX, Loader2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "verifying" : "invalid");
  const [msg, setMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("success");
        if (user) { try { await refreshUser(); } catch {} }
      } catch (err) {
        setStatus("error");
        setMsg(apiErr(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950">
      <div className="w-full max-w-sm fade-up">
        <div className="flex items-center gap-3 mb-8">
          <Swords className="text-amber-500" size={32} />
          <span className="font-cinzel text-2xl text-amber-300 uppercase tracking-widest">Ludus</span>
        </div>

        {status === "verifying" && (
          <div data-testid="verify-verifying" className="flex items-center gap-3 text-zinc-300">
            <Loader2 size={22} className="animate-spin text-amber-400" />
            <p className="text-sm">Verifying your email…</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <div className="flex items-center gap-2 text-amber-400 mb-3">
              <ShieldCheck size={22} />
              <h2 className="font-cinzel text-2xl text-zinc-100">Email verified</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">Your ludus is unlocked. Step into the arena, Lanista.</p>
            <button data-testid="verify-enter-btn" onClick={() => navigate(user ? "/" : "/login")}
              className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all">
              {user ? "Enter the Arena" : "Sign In"}
            </button>
          </div>
        )}

        {(status === "error" || status === "invalid") && (
          <div data-testid="verify-error">
            <div className="flex items-center gap-2 text-red-400 mb-3">
              <MailX size={22} />
              <h2 className="font-cinzel text-2xl text-zinc-100">Verification failed</h2>
            </div>
            <p className="text-zinc-400 text-sm mb-6">
              {status === "invalid" ? "This verification link is missing its token." : msg || "This link is invalid or has expired."}
            </p>
            <button data-testid="verify-to-login-btn" onClick={() => navigate(user ? "/" : "/login")}
              className="w-full bg-amber-900/30 text-amber-300 border border-amber-700/60 px-5 py-3 uppercase tracking-[0.2em] font-cinzel hover:bg-amber-900/50 hover:border-amber-500 transition-all">
              {user ? "Back to my Ludus" : "Sign In"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
